import {useMemo} from 'react'
import {Empty, Space, Table, Tag, Typography} from 'antd'
import {DeleteOutlined, EditOutlined} from '@ant-design/icons'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import RecordsCount from '../../common/RecordsCount'
import TableActions from '../../common/TableActions'
import {useIntl} from 'react-intl'
import {formatEmptyValue, humanizeBool} from '../../../utils/common'

const CognitoClientsTable = ({
  dataSource,
  pagination = false,
  getCognitoClientsListData = null,
  onEdit = null,
  onDelete = null,
}) => {
  const intl = useIntl()

  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'cognitoClients.name' }),
        dataIndex: 'name',
        width: '25%',
        sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'cognitoClients.make' }),
        dataIndex: 'make',
        width: '15%',
        sorter: (a, b) => (a.make || '').localeCompare(b.make || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'cognitoClients.clientId' }),
        dataIndex: 'client_id',
        width: '25%',
        sorter: (a, b) => (a.client_id || '').localeCompare(b.client_id || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'cognitoClients.active' }),
        dataIndex: 'active',
        width: '10%',
        sorter: (a, b) => (a.active || false) === (b.active || false),
        render: (text) => humanizeBool(text),
      },
      {
        title: intl.formatMessage({ id: 'cognitoClients.nativeSignIn' }),
        dataIndex: 'is_allowed_native_sign_in',
        width: '15%',
        sorter: (a, b) =>
          (a.is_allowed_native_sign_in || false) ===
          (b.is_allowed_native_sign_in || false),
        render: (text) => humanizeBool(text),
      },
      {
        title: intl.formatMessage({ id: 'cognitoClients.customLoginColumn' }),
        dataIndex: 'custom_login_enabled',
        width: '12%',
        render: (text) => humanizeBool(text),
      },
      {
        title: intl.formatMessage({ id: 'cognitoClients.identityProviders' }),
        dataIndex: 'providers',
        width: '18%',
        render: (providers) =>
          providers?.length ? (
            <Space size={[4, 4]} wrap>
              {providers.map((provider) => (
                <Tag key={provider.id} data-testid="provider-tag">
                  {provider.display_label || provider.name}
                </Tag>
              ))}
            </Space>
          ) : (
            formatEmptyValue(null)
          ),
      },
      {
        title: intl.formatMessage({ id: 'cognitoClients.action' }),
        align: 'right',
        width: 135,
        render: (_, record) => (
          <TableActions
            actions={[
              {
                key: 'edit',
                label: intl.formatMessage({ id: 'forms.edit' }),
                icon: <EditOutlined />,
                onClick: () => onEdit(record),
              },
              {
                key: 'delete',
                label: intl.formatMessage({ id: 'forms.delete' }),
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => onDelete(record),
              },
            ]}
          />
        ),
      },
    ],
    [dataSource, pagination]
  )

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%', verticalAlign: 'top' }}
    >
      <Table
        rowKey={(record) => `cognito-client-${record.id}`}
        columns={columns}
        dataSource={dataSource}
        showSorterTooltip={false}
        scroll={{
          y: '70vh',
        }}
        pagination={
          pagination
            ? {
                current: pagination?.currentPage,
                total: pagination?.totalCount,
                pageSize: 10,
                showSizeChanger: false,
                hideOnSinglePage: true,
                showTotal: (total) => <RecordsCount count={total} />,
                onChange: (page) => {
                  const offset = (page - 1) * 10
                  const url = pagination?.nextPageLink
                    ? new URL(pagination?.nextPageLink)
                    : new URL(pagination?.prevPageLink)
                  url.searchParams.set('offset', offset)
                  getCognitoClientsListData(url.toString())
                },
              }
            : false
        }
        locale={{
          emptyText: (
            <div style={{ padding: '40px' }}>
              <Empty description={<RecordsCount count={0} />} />
            </div>
          ),
        }}
      />
      {pagination && pagination?.totalPages <= 1 && pagination?.totalCount > 0 && (
        <Typography.Text>
          <RecordsCount count={pagination?.totalCount} />
        </Typography.Text>
      )}
    </Space>
  )
}

export default CognitoClientsTable
